import { type ProductID, type ProductVariantID, getVariantIdFromVariantURI } from '@ifixit/helpers';
import { captureException, SentryError } from '@ifixit/sentry';
import {
   CartWarning,
   type DisplayableError,
   GetCartQuery,
} from '@ifixit/shopify-storefront-client';
import { z, ZodSchema } from 'zod';
import type { Cart, CartLineItem } from '../types';

export type QueryCart = NonNullable<GetCartQuery['cart']>;
type QueryLineItem = QueryCart['lines']['edges'][0]['node'];

type BuildIfixitCartProps = {
   cart: QueryCart | null;
   fallbackCurrencyCode: string;
   userErrors: DisplayableError[];
   validationErrors: string[];
   warnings: CartWarning[];
};
export function buildIfixitCart({
   cart,
   fallbackCurrencyCode,
   userErrors = [],
   validationErrors = [],
   warnings = [],
}: BuildIfixitCartProps): Cart {
   if (cart == null) {
      return emptyCart(fallbackCurrencyCode, userErrors, validationErrors, warnings);
   }
   const subtotalAmount = cart.cost.subtotalAmount;
   const currencyCode =
      subtotalAmount.currencyCode === 'XXX' ? fallbackCurrencyCode : subtotalAmount.currencyCode;
   const compareAtPriceAmount = cart.lines.edges.reduce((sum, { node: lineItem }) => {
      const amountPerQuantity =
         lineItem.cost.compareAtAmountPerQuantity ?? lineItem.merchandise.price;
      return sum + Number.parseFloat(amountPerQuantity.amount) * lineItem.quantity;
   }, 0);
   const discountAmount = compareAtPriceAmount - Number.parseFloat(subtotalAmount.amount);
   const compareAtPrice = {
      amount: compareAtPriceAmount.toFixed(2),
      currencyCode,
   };
   const discount = {
      amount: discountAmount,
      currencyCode,
   };
   return {
      hasItemsInCart: cart.totalQuantity > 0,
      isEmpty: cart.totalQuantity === 0,
      lineItems: cart.lines.edges.map(({ node }) => buildIfixitLineItem(node)),
      totals: {
         discount,
         itemsCount: cart.totalQuantity,
         price: {
            amount: subtotalAmount.amount,
            currencyCode,
         },
         compareAtPrice,
      },
      checkoutUrl: cart.checkoutUrl,
      shopifyCartId: cart.id,
      userErrors,
      validationErrors,
      warnings,
   };
}

function buildIfixitLineItem(lineItem: QueryLineItem): CartLineItem {
   const variant = lineItem.merchandise;
   if (!variant.sku) {
      throw new SentryError('Shopify line item is missing SKU', { extra: { lineItem } });
   }
   const globalVariantId = variant.id as ProductVariantID;
   const variantid = getVariantIdFromVariantURI(globalVariantId);
   const isPreorder = variant.metafieldPreorder?.value === 'true';
   const parsedAnalytics = parseAnalyticsDimensionsMetafield(variant.metafieldAnalyticsDimensions);
   const oemPartnership = parseOemPartnershipMetafield(variant.product.metafieldOemPartnership);
   return {
      shopifyLineId: lineItem.id,
      itemcode: variant.sku,
      variantTitle: variant.title,
      shopifyVariantId: globalVariantId,
      shopifyProductId: variant.product.id as ProductID,
      name: variant.product.title,
      internalDisplayName: variant.metafieldInternalDisplayName?.value,
      imageSrc: variant.image?.url,
      quantity: lineItem.quantity,
      quantityAvailable: (isPreorder ? null : variant.quantityAvailable) ?? undefined,
      price: lineItem.cost.amountPerQuantity,
      compareAtPrice: lineItem.cost.compareAtAmountPerQuantity ?? lineItem.merchandise.price,
      categories: getCategoriesFromAnalyticsDimensions(parsedAnalytics),
      fulfiller: parsedAnalytics?.fulfiller,
      oemPartnership: oemPartnership,
      url: `/products/${variant.product.handle}?variant=${variantid}`,
      isBackordered: variant.metafieldBackordered?.value === 'true',
      isPreorder: variant.metafieldPreorder?.value === 'true',
   };
}

function parseJsonMetafield<T>(
   metafield: { value: string } | null | undefined,
   schema: ZodSchema<T>,
   schemaName: string
): T | null {
   if (metafield == null || !metafield.value) {
      return null;
   }
   let asObj = {};
   try {
      asObj = JSON.parse(metafield.value);
   } catch (error) {
      console.error(`Failed to json parse metafield for ${schemaName}`, { error });
      captureException(
         new SentryError(`Failed to json parse metafield for ${schemaName}`, {
            contexts: {
               'Error Data': {
                  'metafield': metafield,
                  'JSONParseError': error,
               },
            },
         })
      );
      return null;
   }
   try {
      const parsed = schema.parse(asObj);
      return parsed;
   } catch (error) {
      console.error(`Failed to zod parse metafield for ${schemaName}`, { error });
      captureException(
         new SentryError(`Failed to zod parse metafield for ${schemaName}`, {
            contexts: {
               'Error Data': {
                  'metafield': metafield,
                  'Parsed Metafield': asObj,
                  'ZodError': error,
               },
            },
         })
      );
      return null;
   }
}

const AnalyticsDimensionsSchema = z.object({
   is_tool: z.boolean(),
   item_category: z.string(),
   part_subcategory: z.string(),
   fulfiller: z.optional(z.string()),
});

type AnalyticsDimensions = z.infer<typeof AnalyticsDimensionsSchema>;

export function parseAnalyticsDimensionsMetafield(
   metafield: { value: string } | null | undefined
): AnalyticsDimensions | null {
   return parseJsonMetafield(metafield, AnalyticsDimensionsSchema, 'Analytics Dimensions');
}

export const ProductOemPartnershipSchema = z.object({
   code: z.enum([
      'acer',
      'amana',
      'asus',
      'bosch',
      'crosley',
      'crucial',
      'delonghi',
      'electrolux',
      'fairphone',
      'frigidaire',
      'ge',
      'google_pixel',
      'hmd',
      'hp',
      'htc_vive',
      'kenmore',
      'kobo',
      'lenovo',
      'lg',
      'logitech',
      'micron',
      'microsoft',
      'microsoft_surface_tool',
      'motorola',
      'nokia',
      'polaroid',
      'raspberry_pi',
      'samsung',
      'samsung_galaxy',
      'steam_deck',
      'teenage_engineering',
      'valve',
      'whirlpool',
   ]),
   url: z.string().optional().nullable(),
});

export type ProductOemPartnership = z.infer<typeof ProductOemPartnershipSchema>;

export function parseOemPartnershipMetafield(
   metafield: { value: string } | null | undefined
): ProductOemPartnership | null {
   return parseJsonMetafield(metafield, ProductOemPartnershipSchema, 'OEM Partnership');
}

export function getCategoriesFromAnalyticsDimensions(
   dimensions: AnalyticsDimensions | null
): string[] | null {
   return dimensions
      ? [
           dimensions.is_tool ? 'Tool' : 'Part',
           dimensions.item_category,
           dimensions.part_subcategory,
        ]
      : null;
}

function emptyCart(
   fallbackCurrencyCode: string,
   userErrors: DisplayableError[],
   validationErrors: string[],
   warnings: CartWarning[]
): Cart {
   return {
      hasItemsInCart: false,
      isEmpty: true,
      lineItems: [],
      totals: {
         discount: { amount: 0, currencyCode: fallbackCurrencyCode },
         itemsCount: 0,
         price: { amount: '0', currencyCode: fallbackCurrencyCode },
         compareAtPrice: { amount: '0', currencyCode: fallbackCurrencyCode },
      },
      userErrors,
      validationErrors,
      warnings,
   };
}
